/**
 * Authentication Context
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { User } from 'firebase/auth';
import { authManager, AuthResult } from '@/services/AuthManager';
import { userAuth, auth, sendPasswordResetEmail, confirmPasswordReset } from '@/services/firebase-config';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<AuthResult>;
  logout: () => Promise<AuthResult>;
  register: (email: string, password: string, plan?: 'free' | 'paid' | 'poc', language?: 'ja' | 'en') => Promise<AuthResult>;
  resetPassword: (email: string) => Promise<AuthResult>;
  confirmPasswordReset: (oobCode: string, newPassword: string) => Promise<AuthResult>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Initialize auth state listener
    const unsubscribe = userAuth.onAuthStateChanged((user) => {
      setUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<AuthResult> => {
    try {
      setError(null);
      const result = await authManager.signIn(email, password);
      if (!result.success) {
        setError(result.error || 'Login failed');
      }
      return result;
    } catch (err: any) {
      const errorMessage = err.message || 'Login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  const logout = useCallback(async (): Promise<AuthResult> => {
    try {
      setError(null);
      const result = await authManager.signOut();
      if (!result.success) {
        setError(result.error || 'Logout failed');
      }
      return result;
    } catch (err: any) {
      const errorMessage = err.message || 'Logout failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  const register = useCallback(async (
    email: string, 
    password: string, 
    plan: 'free' | 'paid' | 'poc' = 'free',
    language: 'ja' | 'en' = 'ja'
  ): Promise<AuthResult> => {
    try {
      setError(null);
      const result = await authManager.signUp(email, password, plan, language);
      if (!result.success) {
        setError(result.error || 'Registration failed');
      }
      return result;
    } catch (err: any) {
      const errorMessage = err.message || 'Registration failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  const resetPassword = useCallback(async (email: string): Promise<AuthResult> => {
    try {
      setError(null);
      // Get current language from URL
      const pathname = window.location.pathname;
      const params = new URLSearchParams(window.location.search);
      let language = 'ja';
      
      if (pathname.startsWith('/en/') || params.get('lang') === 'en') {
        language = 'en';
      } else if (!pathname.startsWith('/ja/') && !params.get('lang')) {
        // If no explicit language is set, check browser language
        const browserLang = navigator.language || navigator.languages?.[0];
        if (browserLang && browserLang.toLowerCase().startsWith('en')) {
          language = 'en';
        }
      }
      
      // Debug: Log the language being set
      console.log('[Password Reset] Setting language to:', language);
      console.log('[Password Reset] Current auth.languageCode before:', auth.languageCode);
      
      // Set the language code on the auth instance BEFORE sending the email
      // This tells Firebase which language template to use
      auth.languageCode = language;
      
      console.log('[Password Reset] auth.languageCode after setting:', auth.languageCode);
      
      const actionCodeSettings = {
        url: `${window.location.origin}/login?lang=${language}`,
        handleCodeInApp: false
      };
      
      console.log('[Password Reset] Sending email with actionCodeSettings:', actionCodeSettings);
      
      await sendPasswordResetEmail(auth, email, actionCodeSettings);
      
      console.log('[Password Reset] Email sent successfully');
      
      // Reset language code to null after sending (optional)
      auth.languageCode = null;
      
      return { success: true };
    } catch (err: any) {
      // For security, always return success even if email doesn't exist
      if (err.code === 'auth/user-not-found') {
        return { success: true };
      }
      const errorMessage = err.message || 'Failed to send password reset email';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  const confirmPasswordResetHandler = useCallback(async (oobCode: string, newPassword: string): Promise<AuthResult> => {
    try {
      setError(null);
      await confirmPasswordReset(auth, oobCode, newPassword);
      return { success: true };
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to reset password';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: AuthContextValue = {
    user,
    loading,
    error,
    login,
    logout,
    register,
    resetPassword,
    confirmPasswordReset: confirmPasswordResetHandler,
    clearError
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};